import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

/**
 * Single async key/value API backed by the safest storage available:
 *   - Native:  iOS Keychain / Android Keystore via capacitor-secure-storage-plugin.
 *   - Web:     localStorage (no secure equivalent in webview/PWA context).
 *
 * Holds the refresh and SSO session tokens of a remembered session. On native a failed Keychain
 * operation rejects: it is never retried against localStorage, which would put the secret in
 * plaintext storage the app mirrors to Preferences and device backups.
 */

const isNative = (): boolean => Capacitor.isNativePlatform();

/** The plugin rejects reads and removals of a missing key, so callers ask first. */
async function hasNativeKey(key: string): Promise<boolean> {
  const { value: keys } = await SecureStoragePlugin.keys();
  return keys.includes(key);
}

export const secureStorage = {
  async get(key: string): Promise<string | null> {
    if (!isNative()) return localStorage.getItem(key);
    if (!(await hasNativeKey(key))) return null;

    const { value } = await SecureStoragePlugin.get({ key });
    return value;
  },

  async set(key: string, value: string): Promise<void> {
    if (!isNative()) {
      localStorage.setItem(key, value);
      return;
    }

    await SecureStoragePlugin.set({ key, value });
  },

  async remove(key: string): Promise<void> {
    if (!isNative()) {
      localStorage.removeItem(key);
      return;
    }

    if (await hasNativeKey(key)) await SecureStoragePlugin.remove({ key });
  },

  async clear(): Promise<void> {
    if (isNative()) await SecureStoragePlugin.clear();
  },
};
